// eslint-disable-next-line
import React from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"
import { X, AlignLeft } from "lucide-react"

import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  TAB_SIZE,
} from "../../constants/ThemeSetting"
import { onNavStyleChange, toggleCollapsedSideNav } from "appRedux/actions"
import { AppState } from "ReducerState"
import { useResponsive } from "components/responsive"

const SidebarLogo = () => {
  const dispatch = useDispatch()
  const { width, navCollapsed } = useSelector((state: AppState) => state.common)
  let navStyle = useSelector((state: AppState) => state.settings.navStyle)
  const { device } = useResponsive()
  const isIPad = device === "ipad"
  const shouldUseMobileNav = width < TAB_SIZE

  if (width < TAB_SIZE && navStyle === NAV_STYLE_FIXED) {
    navStyle = NAV_STYLE_DRAWER
  }

  const isCollapsed =
    navStyle === NAV_STYLE_MINI_SIDEBAR ||
    navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR
  const isMobileOrDrawer = navStyle === NAV_STYLE_DRAWER || shouldUseMobileNav || isIPad
  const showDesktopToggle =
    (navStyle === NAV_STYLE_FIXED || navStyle === NAV_STYLE_MINI_SIDEBAR) &&
    !shouldUseMobileNav

  const handleCollapseToggle = () => {
    switch (navStyle as string) {
      case NAV_STYLE_DRAWER:
      case NAV_STYLE_NO_HEADER_MINI_SIDEBAR:
        dispatch(toggleCollapsedSideNav(!navCollapsed))
        break
      case NAV_STYLE_FIXED:
        dispatch(onNavStyleChange(NAV_STYLE_MINI_SIDEBAR))
        break
      case NAV_STYLE_MINI_SIDEBAR:
      default:
        dispatch(onNavStyleChange(NAV_STYLE_FIXED))
        break
    }
  }

  return (
    <>
      <style>{`
        @keyframes subtleGlow {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 1; }
        }
        .fromas-version-text {
          animation: subtleGlow 4s ease-in-out infinite;
          animation-delay: 0.5s;
        }
      `}</style>

      {/* ── Expanded header: [☰] | [logo FROMAS v2.x.x] ── */}
      {!isCollapsed && (
        <div
          className="flex items-stretch border-b border-white/10 select-none"
          style={{ backgroundColor: "#003366", minHeight: 56 }}
        >
          {/* Hamburger / close toggle — far left column */}
          {showDesktopToggle && (
            <button
              onClick={handleCollapseToggle}
              className="hidden md:flex items-center justify-center flex-shrink-0 w-10 transition-colors duration-200 group"
              style={{ backgroundColor: "transparent" }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.backgroundColor =
                  "rgba(255,255,255,0.07)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.backgroundColor =
                  "transparent")
              }
              aria-label="Collapse sidebar"
            >
              <AlignLeft className="w-4 h-4 text-white/50 group-hover:text-white/90 transition-colors duration-200" />
            </button>
          )}

          {isMobileOrDrawer && (
            <button
              onClick={() => dispatch(toggleCollapsedSideNav(false))}
              className="flex items-center justify-center flex-shrink-0 w-10 transition-colors duration-200 hover:bg-white/10"
              aria-label="Close sidebar"
            >
              <X className="w-4 h-4 text-white/60 hover:text-white" />
            </button>
          )}

          {/* Thin vertical divider */}
          <div
            className="flex-shrink-0 self-center"
            style={{
              width: 1,
              height: 24,
              backgroundColor: "rgba(255,255,255,0.1)",
            }}
          />

          {/* Logo + Brand */}
          <Link
            to="/main/dashboard"
            className="flex items-center gap-2.5 flex-1 min-w-0 px-3"
            style={{ textDecoration: "none" }}
          >
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
              style={{ backgroundColor: "transparent" }}
            >
              <img
                alt="FROMAS"
                src={`${process.env.PUBLIC_URL}/icon-512x512.png`}
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="flex flex-col min-w-0 leading-none">
              <span
                className="text-white font-bold text-sm uppercase"
                style={{ letterSpacing: "0.12em" }}
              >
                FROMAS
              </span>
              <span className="fromas-version-text text-[10px] text-white/40 tracking-wider mt-0.5">
                version {process.env.REACT_APP_VERSION}
              </span>
            </div>
          </Link>
        </div>
      )}

      {/* ── Collapsed header: hamburger toggle only, no logo ── */}
      {isCollapsed && (
        <div
          className="flex items-center justify-center border-b border-white/10 select-none"
          style={{ backgroundColor: "#003366", minHeight: 56 }}
        >
          {showDesktopToggle && (
            <button
              onClick={handleCollapseToggle}
              className="hidden md:flex items-center justify-center w-10 h-10 rounded-md transition-colors duration-200 group"
              style={{ backgroundColor: "transparent" }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLElement).style.backgroundColor =
                  "rgba(255,255,255,0.07)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLElement).style.backgroundColor =
                  "transparent")
              }
              aria-label="Expand sidebar"
            >
              <AlignLeft className="w-4 h-4 text-white/50 group-hover:text-white/90 transition-colors duration-200" />
            </button>
          )}
        </div>
      )}
    </>
  )
}

export default SidebarLogo
